<script setup lang="ts">
// "Ala-Too dawn" behind the hero board: a drifting blue glow, the sun rising
// behind two mountain ridges with faint snowcaps. Pure decoration — every
// layer is aria-hidden, pointer-events-none, and still for reduced motion.
</script>

<template>
  <div aria-hidden="true" class="pointer-events-none absolute inset-0 overflow-hidden">
    <!-- Drifting sky glow -->
    <div class="hs-aurora absolute -left-28 -top-36 h-[26rem] w-[26rem] rounded-full bg-primary/35 blur-3xl sm:h-[34rem] sm:w-[34rem]" />
    <div class="hs-aurora hs-aurora--late absolute -right-24 top-24 h-72 w-72 rounded-full bg-primary/20 blur-3xl" />

    <!-- The scene: on phones it frames the headline block, on wider screens
         it spans the whole board. -->
    <div class="absolute inset-x-0 top-0 h-[42rem] sm:inset-y-0 sm:h-auto">
      <!-- Sun -->
      <!-- Phones: rising in the top-right corner, in the column the eyebrow
           leaves free (every lower band is full-width text in some language). -->
      <div class="hs-sun absolute -right-5 -top-5 h-24 w-24 sm:right-[30%] sm:top-auto sm:bottom-[26%] sm:h-56 sm:w-56">
        <div class="absolute inset-0 rounded-full bg-sun/40 blur-2xl" />
        <div class="absolute inset-[22%] rounded-full bg-gradient-to-b from-sun to-sun/60" />
      </div>

      <!-- Ridges -->
      <svg class="hs-ridge hs-ridge--back absolute inset-x-0 bottom-0 h-[42%] w-full" viewBox="0 0 1200 300" preserveAspectRatio="none">
        <path d="M0 300 L0 170 L110 110 L190 150 L300 60 L380 120 L470 80 L560 150 L660 70 L760 130 L850 90 L960 160 L1060 100 L1200 150 L1200 300 Z" fill="rgb(var(--color-primary) / 0.22)" />
        <path d="M300 60 L270 85 L292 80 L310 92 L330 78 Z M660 70 L632 94 L655 88 L672 100 L690 86 Z M1060 100 L1036 120 L1056 115 L1072 126 L1088 114 Z" fill="rgb(255 255 255 / 0.14)" />
      </svg>
      <svg class="hs-ridge hs-ridge--front absolute inset-x-0 bottom-0 h-[30%] w-full" viewBox="0 0 1200 220" preserveAspectRatio="none">
        <defs>
          <linearGradient id="hs-front" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stop-color="rgb(var(--color-ink-soft))" />
            <stop offset="1" stop-color="rgb(var(--color-ink))" />
          </linearGradient>
        </defs>
        <path d="M0 220 L0 120 L140 60 L230 110 L350 40 L450 100 L560 70 L680 130 L790 50 L900 110 L1010 75 L1200 120 L1200 220 Z" fill="url(#hs-front)" />
        <path d="M350 40 L326 60 L346 56 L362 66 L378 55 Z M790 50 L768 68 L788 64 L802 74 L818 62 Z" fill="rgb(255 255 255 / 0.2)" />
      </svg>
    </div>
  </div>
</template>

<style scoped>
@media (prefers-reduced-motion: no-preference) {
  .hs-aurora {
    animation: hs-drift 14s ease-in-out infinite alternate;
  }
  .hs-aurora--late {
    animation-duration: 18s;
    animation-direction: alternate-reverse;
  }
  .hs-sun {
    animation: hs-rise 1.6s cubic-bezier(0.16, 1, 0.3, 1) 0.15s both, hs-breathe 6s ease-in-out 1.8s infinite;
  }
  .hs-ridge {
    animation: hs-ridge-in 1.1s cubic-bezier(0.16, 1, 0.3, 1) both;
  }
  .hs-ridge--back {
    animation-delay: 0.05s;
  }
  .hs-ridge--front {
    animation-delay: 0.2s;
  }
}

@keyframes hs-drift {
  from { transform: translate3d(0, 0, 0) scale(1); }
  to { transform: translate3d(3rem, 2rem, 0) scale(1.12); }
}
@keyframes hs-rise {
  from { transform: translate3d(0, 5rem, 0) scale(0.85); opacity: 0; }
  to { transform: translate3d(0, 0, 0) scale(1); opacity: 1; }
}
@keyframes hs-breathe {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
}
@keyframes hs-ridge-in {
  from { transform: translate3d(0, 2.5rem, 0); opacity: 0; }
  to { transform: translate3d(0, 0, 0); opacity: 1; }
}

/* Hold the entrance while the splash curtain is still down. */
:global(.is-splashing) .hs-sun,
:global(.is-splashing) .hs-ridge {
  animation-play-state: paused;
}
</style>
